import { Link, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  KeyRound,
  LogOut,
  Server,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useAuthStore } from '../../../stores/auth-store.js';
import { cn } from '../../../lib/utils.js';
import { Toaster } from 'sonner';
import { logout } from '../../../features/auth/api.js';

interface NavItem {
  to: string;
  icon: typeof Server;
  label: string;
}

const NAV: NavItem[] = [
  { to: '/', icon: Server, label: 'Servidores' },
  { to: '/super-users', icon: Users, label: 'Super Users' },
  { to: '/ia', icon: Sparkles, label: 'Melhoria por IA' },
  { to: '/mcp', icon: KeyRound, label: 'Chaves do MCP' },
  { to: '/auditoria', icon: Activity, label: 'Auditoria' },
];

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const router = useRouterState();
  const currentPath = router.location.pathname;

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch {
      /* mesmo se falhar, limpa local */
    }
    clear();
    window.location.href = '/login';
  };

  return (
    <div className="flex h-screen bg-n-50">
      {/* Sidebar */}
      <aside className="flex w-[220px] flex-col border-r border-n-200 bg-white">
        <div className="flex items-center gap-2.5 border-b border-n-200 px-4 py-4">
          <div className="flex h-[32px] w-[32px] items-center justify-center rounded-md bg-brand-500 text-white">
            <ShieldCheck size={18} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[14px] font-bold text-n-900">ChatPlus</span>
            <span className="text-[11px] font-medium text-brand-600">Super Admin</span>
          </div>
        </div>

        <nav className="flex flex-1 flex-col gap-1 p-2">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active =
              item.to === '/' ? currentPath === '/' : currentPath.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
                  active
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-n-600 hover:bg-n-50 hover:text-n-900',
                )}
              >
                <Icon size={16} strokeWidth={1.75} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-n-200 p-3">
          <div className="mb-2 px-2">
            <div className="text-[13px] font-medium text-n-900">{user?.name}</div>
            <div className="text-[11px] text-n-500">{user?.email}</div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[12px] font-medium text-n-600 hover:bg-n-50 hover:text-danger-600"
          >
            <LogOut size={14} />
            Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto p-6">{children}</main>

      <Toaster richColors position="top-center" closeButton duration={6000} />
    </div>
  );
}
